/**
 * The appearance preference: which of the five themes, applied and remembered.
 *
 * The override is `data-theme` on `<html>`, which the token sheet reads with a
 * higher specificity than the `prefers-color-scheme` media query. Choosing
 * "Sistem" removes the attribute rather than setting a sixth value, so the
 * media query resumes being the only thing deciding - a stored "system" that
 * had to be translated into light or dark would stop following the OS the
 * moment it changed.
 *
 * **`localStorage`, and nothing sensitive in it.** This is a display
 * preference: it is not an account setting, there is no endpoint for it, and
 * writing it to the server would be inventing a settings API to store one
 * enum. It lives under its own key, beside - never mixed with - the remembered
 * email, and it holds one of the known theme strings. No token, no session, no
 * identifier ever goes near this module.
 *
 * Applied before React mounts (see `main.tsx`) so a user who chose Light does
 * not see a dark screen flash on every load.
 */

/**
 * The five themes, plus "system".
 *
 * Order is the order they are offered in: the default first, then the two
 * other dark characters, then light, then the accessibility theme - which
 * goes last not because it matters least but because a picker that opens on
 * an accessibility option is a picker that suggests you need one.
 */
export const THEMES = [
  "system",
  "dark",
  "midnight",
  "light",
  "graphite",
  "contrast",
] as const;
export type Theme = (typeof THEMES)[number];

/** The five real palettes, without the follow-the-OS pseudo-choice. */
export const PALETTE_THEMES = THEMES.filter(
  (theme): theme is Exclude<Theme, "system"> => theme !== "system",
);

export const THEME_LABELS: Record<Theme, string> = {
  system: "Sistem",
  dark: "SentyCap Koyu",
  midnight: "Gece Yarısı",
  light: "Açık",
  graphite: "Grafit",
  contrast: "Yüksek Kontrast",
};

/**
 * One line each, describing the character rather than the colour. "Mavi" on a
 * swatch that is visibly blue is a caption; this is the reason to pick it.
 */
export const THEME_DESCRIPTIONS: Record<Theme, string> = {
  system: "İşletim sisteminizin açık/koyu tercihini izler.",
  dark: "Varsayılan çalışma yüzeyi: soğuk arduvaz, ölçülü derinlik.",
  midnight: "Neredeyse siyah; 3B sahnelerde geometriyi öne çıkarır.",
  light: "Aydınlık ortamlar için gerçek açık tema, kağıt hissi.",
  graphite: "Nötr ve teknik gri; keskin köşeler, düz yüzeyler.",
  contrast: "Erişilebilirlik odaklı; en güçlü metin ve odak kontrastı.",
};

/**
 * Themes that were shipped under an earlier name.
 *
 * A preference already written to a browser has to keep working: someone who
 * chose "SentyCap Obsidian" gets Midnight, which is that palette continued,
 * rather than being silently reset to the default. The migration happens on
 * read and is written back on the next explicit choice.
 */
const LEGACY_ALIASES: Record<string, Theme> = {
  obsidian: "midnight",
  titanium: "graphite",
};

/** Its own key. Never the one holding the remembered email. */
const STORAGE_KEY = "sentycap.appearance";
const DEFAULT: Theme = "system";

const isTheme = (value: unknown): value is Theme =>
  typeof value === "string" && (THEMES as readonly string[]).includes(value);

export function readTheme(): Theme {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isTheme(stored)) return stored;
    if (typeof stored === "string" && stored in LEGACY_ALIASES) {
      return LEGACY_ALIASES[stored]!;
    }
    // Anything else - a value from a future build, a hand-edited key, a
    // truncated write - is not a theme. The default is the safe answer, and
    // it is not an error worth showing anyone.
    return DEFAULT;
  } catch {
    // Safari in private mode throws on `localStorage`. A preference that
    // cannot be read is simply the default.
    return DEFAULT;
  }
}

/**
 * Put the choice on the document.
 *
 * Separated from storage so it can run before React and before anything is
 * persisted - the entry point calls this with `readTheme()` synchronously.
 */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  if (theme === "system") root.removeAttribute("data-theme");
  else root.setAttribute("data-theme", theme);
}

export function writeTheme(theme: Theme): void {
  applyTheme(theme);
  try {
    if (theme === DEFAULT) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // The theme is already applied; failing to remember it is not a failure
    // worth interrupting anyone over.
  }
}
